import { useCoachingInsights } from '../../lib/useCoachingInsights';
import { useT } from '../../lib/i18n';

// Coaching Insights tile — surfaces the highest-priority cross-signal
// pattern the rule engine matched. Sits on the Today screen near the
// NextMilestonesTile so daily-action context lives in one place.
//
// Visual treatment varies by tone:
//   - concern (something to fix): amber border + alert icon
//   - opportunity (something to lean into): blue border + lightbulb
//   - celebration (something working): emerald border + spark
//
// Suppresses itself entirely when no rule matches OR data is too thin
// (under 30 visits in the last 90 days). Quiet by default is the right
// default — we never want a "nothing to coach today" filler card.

export function CoachingInsightsTile() {
  const t = useT();
  const { insight, loading } = useCoachingInsights();

  if (loading) {
    return (
      <div className="rounded-2xl border border-line bg-surface animate-pulse h-32" />
    );
  }
  if (!insight) return null;

  const tone = insight.tone;
  const border =
    tone === 'concern'
      ? 'border-amber-300 dark:border-amber-700/50'
      : tone === 'opportunity'
        ? 'border-navy-900/20 dark:border-gold-500/30'
        : 'border-emerald-300 dark:border-emerald-700/50';
  const bg =
    tone === 'concern'
      ? 'bg-amber-50/60 dark:bg-amber-900/15'
      : tone === 'opportunity'
        ? 'bg-bg-alt'
        : 'bg-emerald-50/60 dark:bg-emerald-900/15';
  const iconCls =
    tone === 'concern'
      ? 'text-amber-700 dark:text-amber-400'
      : tone === 'opportunity'
        ? 'text-navy-900 dark:text-gold-400'
        : 'text-emerald-700 dark:text-emerald-400';
  const eyebrowText =
    tone === 'concern'
      ? t('insight.eyebrow.concern')
      : tone === 'opportunity'
        ? t('insight.eyebrow.opportunity')
        : t('insight.eyebrow.celebration');

  return (
    <div
      role="region"
      aria-label={t('insight.aria')}
      className={`rounded-2xl border-2 ${border} ${bg} p-4`}
    >
      <div className={`text-[10px] tracking-[0.16em] uppercase font-extrabold ${iconCls} inline-flex items-center gap-1.5 mb-1.5`}>
        <ToneIcon tone={tone} className="w-3.5 h-3.5" />
        {eyebrowText}
      </div>
      <h2 className="font-display text-lg leading-tight mb-2">
        {t(insight.titleKey, insight.vars)}
      </h2>
      <p className="text-[13px] text-ink-700 dark:text-ink-300 leading-snug">
        {t(insight.bodyKey, insight.vars)}
      </p>
      {insight.bulletKeys && insight.bulletKeys.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {insight.bulletKeys.map((key) => (
            <li
              key={key}
              className="flex items-start gap-2 text-[12px] text-ink-700 dark:text-ink-300 leading-snug"
            >
              <span
                className={`inline-block w-1 h-1 rounded-full mt-2 shrink-0 ${
                  tone === 'concern'
                    ? 'bg-amber-600'
                    : tone === 'opportunity'
                      ? 'bg-navy-900 dark:bg-gold-400'
                      : 'bg-emerald-600'
                }`}
                aria-hidden
              />
              <span>{t(key, insight.vars)}</span>
            </li>
          ))}
        </ul>
      )}
      {insight.lessonKey && (
        // Lesson CTA — present but not yet routed to a specific lesson
        // (lessons system has its own picker). v1.1 wires this to a
        // direct lesson route once we tag lessons with insight kinds.
        <div className="mt-3 text-[11px] text-ink-500 italic leading-snug">
          {t(insight.lessonKey, insight.vars)}
        </div>
      )}
    </div>
  );
}

function ToneIcon({
  tone,
  className,
}: {
  tone: 'concern' | 'opportunity' | 'celebration';
  className?: string;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {tone === 'concern' && (
        <>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
      )}
      {tone === 'opportunity' && (
        <>
          <path d="M9 18h6" />
          <path d="M10 22h4" />
          <path d="M12 2a7 7 0 0 0-4 12.5c.5.5 1 1 1 1.5v2h6v-2c0-.5.5-1 1-1.5A7 7 0 0 0 12 2z" />
        </>
      )}
      {tone === 'celebration' && (
        <>
          <polygon points="12 2 15 8 22 9 17 14 18 21 12 17 6 21 7 14 2 9 9 8 12 2" />
        </>
      )}
    </svg>
  );
}
